import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BLOCK_META } from './block-library';

import type { BlockType } from '@/types/experience';

/**
 * THE QUICK-ADD BAR — the dashed strip at the foot of a list of blocks.
 *
 * One place a block list grows from, wherever a block list is. The canvas has had this shape
 * since the builder shipped and teachers read it without being told: a dashed outline (so it
 * reads as the empty slot after the content rather than as content), a leading `+`, and one
 * evenly-weighted column per type.
 *
 * Extracted because the exit editor needed the same bar and the alternative was a second
 * spelling of it. The two surfaces differ only in what they offer and what they do at the end
 * of the row, so those are the props; everything that makes it recognisable is here and cannot
 * drift between them.
 *
 * `disabled` greys every column rather than hiding the bar: a control that vanishes reads as a
 * bug, and the caller pairs it with `describedBy` so the greying has a stated reason.
 */
export function QuickAddBar({
  types, onAdd, short = false, highlight = false, disabled = false, bare = false, describedBy, className = '', children,
}: {
  types: BlockType[];
  onAdd: (type: BlockType) => void;
  /** Short labels, for a phone in portrait where five full names do not fit across. */
  short?: boolean;
  /** Something draggable is in flight over this bar — the canvas's drop-target tint. */
  highlight?: boolean;
  /** Nothing more may be added. */
  disabled?: boolean;
  /**
   * No outline — for a host that is already a band.
   *
   * The dashed box says "the empty slot after the content" on a canvas, where nothing else
   * marks the end of the list. In a banded dialog the rule above the band says it, and the
   * dashes were a second boundary drawn inside the first.
   */
  bare?: boolean;
  /** Where the reason for `disabled` is written, so the greyed buttons point at it. */
  describedBy?: string;
  className?: string;
  /** Whatever follows the columns — the canvas's library opener, and nothing else so far. */
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      role='group'
      aria-label={t('task.quickAdd.groupLabel')}
      className={`flex items-center gap-1 transition-colors ${
        bare ? '' : `rounded-xl border border-dashed px-2 py-2 ${highlight ? 'border-ws-primary' : 'border-ws-border'}`
      } ${className}`}
    >
      <Plus aria-hidden='true' className='mx-1 h-4 w-4 shrink-0 text-ws-muted' />
      {types.map(type => {
        const meta = BLOCK_META[type];
        if (!meta) return null;
        const { Icon, labelKey, shortLabelKey } = meta;
        return (
          <button
            key={type}
            type='button'
            aria-label={t('task.quickAdd.addBlock', { label: t(labelKey) })}
            aria-describedby={describedBy}
            disabled={disabled}
            onClick={() => onAdd(type)}
            className='flex flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2 text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-primary disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ws-muted'
          >
            <Icon aria-hidden='true' className='h-4 w-4' />
            <span className='text-xs'>{t(short ? shortLabelKey : labelKey)}</span>
          </button>
        );
      })}
      {children}
    </div>
  );
}
